/**
 * WebSocket facade - delegates to SocketService (single connection).
 * CONNECTED only after server connectionSuccess. Use requestJoinConversation for join.
 * See FRONTEND_WEBSOCKET_COMPLETE_INTEGRATION_GUIDE and shared/services/socket/.
 */

import { getAccessTokenToCookies, getAccessToken } from '@/shared/utils/tokenHelper';
import tokenRefreshEmitter from '@/shared/services/websocketTokenManager';
import { getSocketServiceInstance } from '@/shared/services/socket/SocketService';
import { liveSocketKeepAlive } from '@/domains/live/services/liveSocketKeepAlive';
import {
  RESUME_RECONNECT_DELAY_MS,
  RESUME_DEBOUNCE_MS,
} from '@/shared/services/socket/constants';
import { shouldPreventApiCallSafe, isAuthLoggedOutSafe } from '@/shared/utils/piniaSafe.util';

function getAuthToken() {
  const useCookies = process.env.VUE_APP_USE_COOKIES === 'true';
  const token = useCookies ? getAccessTokenToCookies() : getAccessToken();
  if (!token || token === 'null' || token === 'undefined' || String(token).trim() === '') {
    return null;
  }
  return token;
}

function getCore() {
  return getSocketServiceInstance();
}

function shouldSkipWebSocketConnect() {
  if (isAuthLoggedOutSafe() || shouldPreventApiCallSafe()) return true;
  return !getAuthToken();
}

function checkAndConnect() {
  if (shouldSkipWebSocketConnect()) return;

  const core = getCore();
  if (core.isSessionSuperseded?.()) return;

  const token = getAuthToken();
  const state = core.getState();
  if (token && !core.isConnected() && state !== 'CONNECTING' && state !== 'RECONNECTING') {
    core.connect();
  }
}

let lifecycleStarted = false;

/** Start socket polling + resume listeners after Pinia is installed (call from main.js). */
export function initWebSocketLifecycle() {
  if (lifecycleStarted || typeof globalThis.window === 'undefined') return;
  lifecycleStarted = true;

  checkAndConnect();
  const tokenCheckInterval = setInterval(checkAndConnect, 5000);
  globalThis.window.addEventListener('beforeunload', () => clearInterval(tokenCheckInterval));

  let resumeTimer = null;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (shouldSkipWebSocketConnect()) return;
    if (liveSocketKeepAlive.hasActiveLiveSession) return;
    if (resumeTimer) clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => {
      resumeTimer = null;
      const core = getCore();
      if (core.isSessionSuperseded?.()) return;
      if (!getAuthToken()) return;
      if (!core.isConnected() && core.getState() !== 'CONNECTING' && core.getState() !== 'RECONNECTING') {
        core.connect();
      }
    }, RESUME_RECONNECT_DELAY_MS + RESUME_DEBOUNCE_MS);
  });
}

const socketFacade = {
  get connected() {
    return getCore().isConnected();
  },

  on(event, handler) {
    getCore().on(event, handler);
  },

  off(event, handler) {
    getCore().off(event, handler);
  },

  emit(event, data, ack) {
    const c = getCore();
    if (event === 'joinConversation' && data?.conversationId) {
      c.requestJoinConversation(data.conversationId);
      if (typeof ack === 'function') ack({ success: true });
      return;
    }
    if (event === 'leaveConversation' && data?.conversationId) {
      c.leaveConversation(data.conversationId);
      if (typeof ack === 'function') ack({ success: true });
      return;
    }
    c.emit(event, data, ack);
  },

  leaveConversation(conversationId) {
    getCore().leaveConversation(conversationId);
  },

  requestJoinConversation(conversationId) {
    getCore().requestJoinConversation(conversationId);
  },
};

tokenRefreshEmitter.onTokenRefreshed((newToken) => {
  getCore().updateToken(newToken);
});

export const disconnectSocket = () => {
  getCore().disconnect();
};

/** Push a refreshed JWT to the socket without forcing a full reconnect handshake. */
export const pushRefreshedTokenToSocket = (accessToken) => {
  const token = accessToken || getAuthToken();
  if (token) {
    tokenRefreshEmitter.emitTokenRefreshed(token);
  }
};

/** Re-handshake with latest JWT; preserves open conversation join on connectionSuccess. */
export const reconnectSocket = () => {
  getCore().reconnectWithFreshToken();
};

export default socketFacade;
